import { callDockAnchorAtom, callStore } from '@/features/interview-scheduling/call-store'
import { useScheduledJobillaMeet } from '@/features/interview-scheduling/use-scheduled-jobilla-meet'
import { Button, logger } from '@/ui'
import { candidateEventIndexQueryKey, useRecruiterBookingInstant } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useSetAtom } from 'jotai'
import { CalendarClockIcon, CheckIcon, CopyIcon, VideoIcon } from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface JobillaMeetTabProps {
	candidateId: string
	campaignId: string
	/** A call is already live (shown by the docked CallDock overlay), so the body steps aside. */
	hasActiveCall: boolean
	/** Hands the recruiter's join URL up to the candidate card, which owns the persistent call. */
	onStartCall: (joinUrl: string) => void
}

/**
 * The candidate card's Jobilla Meet tab body. When a confirmed interview is within reach it
 * offers to join the call; otherwise it offers to start an instant call — a real, confirmed
 * interview scheduled for now that sends the candidate their join link on the spot. The call
 * itself is owned by the card (so it survives tab switches as a popout) — this only triggers it.
 * The 30-minute "within reach" threshold lives in useScheduledJobillaMeet.
 */
export function JobillaMeetTab({ candidateId, campaignId, hasActiveCall, onStartCall }: JobillaMeetTabProps) {
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const scheduled = useScheduledJobillaMeet(candidateId)

	const startInstant = useRecruiterBookingInstant({
		onSuccess: (data) => {
			logger.track('interviews:instant_call_started', {
				data: { candidate_id: candidateId, campaign_id: campaignId, booking_id: data.id },
			})
			// The timeline + the scheduled-meeting lookup both read the candidate-events feed,
			// so refresh it to surface the new booking on the timeline.
			void queryClient.invalidateQueries({ queryKey: candidateEventIndexQueryKey(candidateId) })
			if (data.meeting.recruiterLink !== null) {
				onStartCall(data.meeting.recruiterLink)
			}
		},
		onError: (error) => {
			const status = (error as { response?: { status?: number } }).response?.status
			// 403 = org can't use Jobilla Meet (flag off or not entitled); expected, not alert-worthy.
			if (status !== 403) {
				logger.error(error, {
					tags: { component: 'JobillaMeetTab', action: 'startInstant' },
					data: { candidateId, campaignId },
				})
			}
		},
	})

	const setDockAnchor = useSetAtom(callDockAnchorAtom, { store: callStore })
	const dockAnchorRef = useCallback(
		(element: HTMLDivElement | null) => setDockAnchor(element),
		[setDockAnchor],
	)

	const candidateLink = scheduled?.meeting.candidateLink ?? null

	// The live call is rendered app-wide by GlobalCallDock; while this tab is open the call docks
	// over the anchor (which fills the tab above the candidate link), and pops out to the corner
	// once we navigate away. The candidate's join link sits below so the recruiter can share it too.
	if (hasActiveCall) {
		return (
			<div className="flex h-full w-full flex-col">
				<div ref={dockAnchorRef} className="min-h-0 flex-1" />
				{candidateLink !== null
					? (
						<CandidateMeetingLinkCopy
							candidateLink={candidateLink}
							className="shrink-0 border-t border-grey-200 px-3 py-2.5"
						/>
					)
					: null}
			</div>
		)
	}

	const scheduledJoinLink = scheduled?.meeting.recruiterLink ?? null

	// A scheduled interview within reach takes over the tab; only when there's nothing to join
	// (or it's still more than 30 minutes off) do we offer to start an instant call.
	if (scheduled !== null && !scheduled.isFarOff) {
		if (scheduled.isJoinable && scheduledJoinLink !== null) {
			return (
				<MeetPrompt
					icon={<VideoIcon className="size-10 text-brand-500" aria-hidden="true" />}
					title={t('interviews.jobillaMeet.readyTitle')}
					body={t('interviews.jobillaMeet.readyBody')}
					action={
						<Button type="committal" size="short" onClick={() => onStartCall(scheduledJoinLink)}>
							{t('interviews.jobillaMeet.joinNow')}
						</Button>
					}
					candidateLink={candidateLink}
				/>
			)
		}

		return (
			<MeetPrompt
				icon={<CalendarClockIcon className="size-10 text-grey-300" aria-hidden="true" />}
				title={t('interviews.jobillaMeet.scheduledTitle')}
				body={t('interviews.jobillaMeet.scheduledBody', {
					time: formatDistanceToNow(scheduled.meeting.joinableAt, { addSuffix: true }),
				})}
				candidateLink={candidateLink}
			/>
		)
	}

	return (
		<MeetPrompt
			icon={<VideoIcon className="size-10 text-grey-300" aria-hidden="true" />}
			title={t('interviews.jobillaMeet.startTitle')}
			body={t('interviews.jobillaMeet.startBody')}
			action={
				<Button
					type="committal"
					size="short"
					isLoading={startInstant.isPending}
					onClick={() => startInstant.mutate({ campaignId, candidateId })}
				>
					{t('interviews.jobillaMeet.sendLinkNow')}
				</Button>
			}
			error={startInstant.isError ? t('interviews.jobillaMeet.error') : null}
		/>
	)
}

function MeetPrompt({ icon, title, body, action, error, candidateLink }: {
	icon: ReactNode
	title: string
	body: string
	action?: ReactNode
	error?: string | null
	candidateLink?: string | null
}) {
	return (
		<div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
			{icon}
			<h3 className="text-base font-medium text-grey-800">{title}</h3>
			<p className="max-w-sm text-sm text-grey-500">{body}</p>
			{action}
			{error != null ? <p className="text-sm text-red-600">{error}</p> : null}
			{candidateLink != null
				? <CandidateMeetingLinkCopy candidateLink={candidateLink} className="mt-2 w-full max-w-sm text-left" />
				: null}
		</div>
	)
}

/**
 * The candidate's own join link with a copy button, shown wherever a Jobilla Meet call is
 * live or ready so the recruiter can copy and re-share it (e.g. paste it into a chat)
 * alongside their own host link.
 */
function CandidateMeetingLinkCopy({ candidateLink, className }: { candidateLink: string; className?: string }) {
	const { t } = useTranslation('common')
	const [copied, setCopied] = useState(false)

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(candidateLink)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch (error) {
			logger.error(error, { tags: { component: 'JobillaMeetTab', action: 'copyCandidateLink' } })
		}
	}, [candidateLink])

	return (
		<div className={className}>
			<div className="mb-1 text-xs font-medium text-grey-500">
				{t('interviews.timeline.candidateMeetingLink')}
			</div>
			<div className="flex items-center gap-2">
				<span className="min-w-0 flex-1 truncate font-mono text-xs text-grey-400" title={candidateLink}>
					{candidateLink}
				</span>
				<button
					type="button"
					onClick={handleCopy}
					className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-grey-200 px-2 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700"
				>
					{copied
						? (
							<>
								<CheckIcon className="size-3.5 text-green-500" aria-hidden="true" />
								{t('interviews.copied')}
							</>
						)
						: (
							<>
								<CopyIcon className="size-3.5" aria-hidden="true" />
								{t('interviews.copyUrl')}
							</>
						)}
				</button>
			</div>
		</div>
	)
}
